import React, { useEffect, useState } from "react";
import Breadcrumb from "../../../components/common/BreadCrumb";
import Action from "../../../components/common/Action";
import { Badge } from "../../../components/micro/input";
import { SortDownArrow } from "../../../assets/svg";
import CustomTable from "../../../components/customTable/CustomTable";
import DebounceSearch from "../../../components/common/DebounceSearch";
import ReactSelect from "react-select";
import { useDispatch, useSelector } from "react-redux";
import {
  getServiceReportList,
  getTechnicianList,
  markAsCompleted,
} from "../../../redux/service/slice";
import RippleLoader from "../../../components/RippleLoader/RippleLoader";
import ViewServiceReport from "./ViewServiceReport";
import ServiceCompleteModal from "./ServiceCompleteModal";

const ServiceReport = () => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [technician, setTechnician] = useState("");
  const [isModal, setIsModal] = useState(false);
  const [isMarkModal, setIsMarkModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSaleId, setSelectedSaleId] = useState(null);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [serviceCount, setServiceCount] = useState("");
  const [selectedAction, setSelectedAction] = useState(null);
  const [showSend, setShowSend] = useState("");
  const [showErrorPopup, setShowErrorPopup] = useState(false);

  // selector
  const { ServiceReportList, ServiceReportListLoading, techniciansList } =
    useSelector((store) => ({
      ServiceReportList:
        store?.serviceData?.getServiceReportListStatus?.data?.data,
      ServiceReportListLoading: store?.serviceData?.loading,
      techniciansList: store?.technicianData?.getTechnicianListStatus?.data,
    }));

  //Get getServiceReportList List
  useEffect(() => {
    dispatch(
      getServiceReportList({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
        technician: technician?.label || "",
        status: selectedStatus?.value || "",
      })
    );
    setSelectedAction("");
    setServiceCount("");
    setSelectedRows([]);
  }, [
    dispatch,
    searchValue,
    pageCount,
    pageLimit,
    sortBy,
    sortDirection,
    technician,
    selectedStatus,
    selectedAction,
    serviceCount,
  ]);

  // Technician list api
  useEffect(() => {
    dispatch(
      getTechnicianList({
        pageLimit: "all",
      })
    );
  }, [dispatch]);

  const techniciansOptions = Array.isArray(
    techniciansList?.data || techniciansList
  )
    ? (techniciansList?.data || techniciansList)?.map((technician) => ({
        value: technician.id,
        label: technician.full_name,
      }))
    : [];

  const handleTechnicianChange = (selectedOption) => {
    setTechnician(selectedOption);
  };

  const handleConfirm = async () => {
    setIsLoading(true);

    const action = markAsCompleted(selectedSaleId);

    setIsLoading(true);
    try {
      const res = await dispatch(action);
      if (res.type === "markAsCompleted/fulfilled") {
        setIsMarkModal(false);
        dispatch(
          getServiceReportList({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
            technician: technician?.label || "",
            status: selectedStatus?.value || "",
          })
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRowSelection = (id, isChecked) => {
    setSelectedRows((prevSelected) => {
      let updatedSelection;

      if (isChecked) {
        updatedSelection = [...prevSelected, id];
      } else {
        updatedSelection = prevSelected.filter((rowId) => rowId !== id);
        setSelectedAction("");
        setServiceCount("");
        setSelectedRows([]);
      }

      // Update isSelectAll state
      setIsSelectAll(
        updatedSelection.length === ServiceReportList?.data?.length
      );

      return updatedSelection;
    });
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setIsSelectAll(checked);

    if (checked) {
      setSelectedRows(ServiceReportList?.data?.map((item) => item));
    } else {
      setSelectedRows([]);
      setSelectedAction("");
      setServiceCount("");
      setSelectedRows([]);
    }
  };

  const bulkOptions = () => {
    return [
      { id: 1, value: "send report", label: "Send Report" },
      { id: 2, value: "view report", label: "View Report" },
    ];
  };

  const handleBulkAction = (e) => {
    setSelectedAction(e);

    const selectedIds = selectedRows.map((row) => row.id);

    const selectedData = ServiceReportList?.data?.filter((item) =>
      selectedIds.includes(item.id)
    );

    if (!selectedData || selectedData.length === 0) return;

    if (e?.value === "send report" || e?.value === "view report") {
      const uniqueCompanies = [
        ...new Set(selectedData.map((item) => item?.company_id)),
      ];

      if (uniqueCompanies.length === 1) {
        setShowSend("bulk");
        setSelectedSaleId(selectedIds);
        setIsModal(true);
      } else {
        setShowErrorPopup(true);
      }
    }
  };

  const Status = [
    { id: 1, value: "pending signature", label: "Pending Signature" },
    { id: 2, value: "acknowledged", label: "Acknowledged" },
  ];

  const ColumnHeaders = () => (
    <>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div className="flex items-center">
          <div className="flex items-center">
            <input
              type="checkbox"
              className="w-4 h-4 mr-2 accent-themeBtn"
              checked={isSelectAll}
              onChange={handleSelectAll}
            />{" "}
            <div
              className="flex items-center"
              onClick={() => {
                setSortBy("id");
                setSortDirection(sortDirection === "desc" ? "asc" : "desc");
              }}
            >
              <span>No.</span>

              <SortDownArrow
                className={`ml-1 transition-transform duration-300 ${
                  sortDirection === "asc" && sortBy === "id" ? "rotate-180" : ""
                }`}
              />
            </div>
          </div>
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("company");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Company</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "company"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("outlet");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Outlet</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "outlet" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("address");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Address</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "address"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("date_time");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Date & Time</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "date_time"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("technician");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Technician</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "technician"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("status");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Status </span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "status" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Types
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Action
      </th>
    </>
  );

  const DataRows = () => (
    <>
      {ServiceReportList?.data?.map((element, index) => (
        <tr
          key={index}
          className="font-Proxima text-[14px] shadow-sm text-mgrey hover:bg-lightprimaryorange"
        >
          <td className="px-4 py-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                className="w-4 h-4 mr-2 accent-themeBtn"
                onChange={(e) => handleRowSelection(element, e.target.checked)}
                checked={selectedRows?.includes(element)}
              />
              {element?.unique_id ?? "-"}
            </div>
          </td>
          <td className="px-2 py-3 truncate">
            {element?.company_name
              ? element?.company_name
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase())
              : "-"}
          </td>
          <td className="px-2 py-3 ">
            <div className="truncate-lines-3">
              {element?.outlet_name
                ? element?.outlet_name
                    .toLowerCase()
                    .replace(/\b\w/g, (char) => char.toUpperCase())
                : "-"}
            </div>
          </td>
          <td className="px-2 py-3 ">
            <div className="truncate-lines-3">
              {element?.outlet_address
                ? element?.outlet_address
                    .toLowerCase()
                    .replace(/\b\w/g, (char) => char.toUpperCase())
                : "-"}
            </div>
          </td>
          <td className="px-5 py-3 ">{element?.DateTime}</td>
          <td className="px-2 py-3">
            {element?.technician_name && element.technician_name.trim()
              ? element.technician_name
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase())
              : "-"}
          </td>
          <td className="px-2 py-3">
            <div className="flex justify-start">
              <Badge
                className={`capitalize ${
                  element?.status === "pending signature" ||
                  element?.status === "acknowledged"
                    ? "border-borderGreen text-bgGreen"
                    : element?.status === "closed"
                    ? "bg-bgGreen text-white"
                    : "-"
                }`}
                value={
                  element?.status === "acknowledged"
                    ? "Acknowledged"
                    : element?.status === "pending signature"
                    ? "Pending Signature"
                    : element?.status === "closed"
                    ? "Closed"
                    : "-"
                }
              />
            </div>
          </td>
          <td className="px-2 py-3">{element?.types || "-"}</td>
          <td className="flex px-2 py-3">
            {element?.status === "pending signature" ? (
              <Action
                optionList={
                  <>
                    <li
                      className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                      onClick={() => {
                        setSelectedSaleId([element?.id]);
                        setIsModal(true);
                      }}
                    >
                      View Report
                    </li>
                    <li
                      className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                      onClick={() => {
                        setSelectedSaleId([element?.id]);
                        setIsModal(true);
                      }}
                    >
                      Send Report
                    </li>
                    <li
                      className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                      onClick={() => {
                        setSelectedSaleId([element?.id]);
                        setIsMarkModal(true);
                        setServiceCount(element?.service_count);
                      }}
                    >
                      Mark As Completed
                    </li>
                  </>
                }
              />
            ) : (
              <Action
                optionList={
                  <>
                    <li
                      className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                      onClick={() => {
                        setSelectedSaleId([element?.id]);
                        setIsModal(true);
                      }}
                    >
                      View Report
                    </li>
                    <li
                      className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                      onClick={() => {
                        setSelectedSaleId([element?.id]);
                        setIsModal(true);
                      }}
                    >
                      Send Report
                    </li>
                  </>
                }
              />
            )}
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <>
      <div>
        <div>
          <h6 className="font-Montserrat font-medium text-xl mb-3">
            Service Report
          </h6>
          <Breadcrumb
            breadCrumbParent={"Home"}
            breadCrumbActive={"Service Report"}
            breadCrumbParent2={"Service Request"}
            breadCrumbTitleKey={"/"}
            classes=""
          />
        </div>
        <div className="flex items-center space-x-3 mt-8">
          <ReactSelect
            className={"w-full max-w-52 text-black p-0 font-Proxima"}
            id="option"
            placeholder="Bulk Action"
            options={bulkOptions(selectedRows)}
            isClearable={true}
            value={selectedAction}
            isDisabled={selectedRows?.length === 0}
            onChange={(e) => handleBulkAction(e)}
            styles={{
              control: (base) => ({
                ...base,
                border: `1px solid #0000003B`,
                borderColor: selectedAction ? "#F15B29" : "#0000003B",
                borderRadius: "0.3rem",
                boxShadow: "none",
                "&:hover": {
                  border: "1px solid #0000003B",
                },
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? "#F15B29" : "white",
                "&:active": {
                  backgroundColor: state.isSelected
                    ? "#F15B29"
                    : "rgba(0, 0, 0, 0.04)",
                },
              }),
            }}
          />
          <ReactSelect
            className={"w-full max-w-52 text-black p-0 font-Proxima"}
            id="technician"
            placeholder="Technician"
            options={techniciansOptions}
            isClearable={true}
            value={technician}
            onChange={handleTechnicianChange}
            styles={{
              control: (base) => ({
                ...base,
                border: `1px solid #0000003B`,
                borderColor: technician ? "#F15B29" : "#0000003B",
                borderRadius: "0.3rem",
                boxShadow: "none",
                "&:hover": {
                  border: "1px solid #0000003B",
                },
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? "#F15B29" : "white",
                "&:active": {
                  backgroundColor: state.isSelected
                    ? "#F15B29"
                    : "rgba(0, 0, 0, 0.04)",
                },
              }),
            }}
          />
          <ReactSelect
            className={"w-full max-w-52 text-black p-0 font-Proxima"}
            id="status"
            placeholder="Status"
            options={Status}
            isClearable={true}
            value={selectedStatus}
            onChange={(e) => {
              setSelectedStatus(e);
            }}
            styles={{
              control: (base) => ({
                ...base,
                border: `1px solid #0000003B`,
                borderColor: selectedStatus ? "#F15B29" : "#0000003B",
                borderRadius: "0.3rem",

                boxShadow: "none",
                "&:hover": {
                  border: "1px solid #0000003B",
                },
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? "#F15B29" : "white",
                "& :active": {
                  backgroundColor: state.isSelected
                    ? "#F15B29"
                    : "rgba(0, 0, 0, 0.04)",
                },
              }),
            }}
          />
          <DebounceSearch
            className="full-width"
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
        </div>
        {ServiceReportListLoading ? (
          <RippleLoader />
        ) : (
          <div className="w-full bg-white shadow rounded-lg mt-5 ">
            <CustomTable
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              data={ServiceReportList}
              loading={false}
              showPagination={true}
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              current={pageCount}
              total={ServiceReportList?.total}
              paginationData={(crPage) => setPageCount(crPage)}
              lastPage={Math.ceil(ServiceReportList?.total / pageLimit)}
            />
          </div>
        )}
      </div>
      {isMarkModal && (
        <div>
          <ServiceCompleteModal
            onClose={() => {
              setIsMarkModal(false);
            }}
            isLoading={isLoading}
            confirmText={
              serviceCount === 1
                ? "Service Count Updated For Maintenance Service"
                : "No Service Count Deducted For Repair"
            }
            onConfirm={() => {
              if (serviceCount === 1) {
                handleConfirm();
              } else {
                setIsMarkModal(false);
              }
            }}
          />
        </div>
      )}
      {isModal && (
        <div>
          <ViewServiceReport
            onClose={() => {
              setIsModal(false);
            }}
            selectedSaleId={selectedSaleId}
            setIsLoading={setIsLoading}
            isLoading={isLoading}
            showSend={showSend}
            setSelectedAction={setSelectedAction}
            setServiceCount={setServiceCount}
            setSelectedRows={setSelectedRows}
          />
        </div>
      )}
      {showErrorPopup && (
        <div className="model" onClick={() => setShowErrorPopup(false)}>
          <div
            className="bg-white flex flex-col items-center justify-center drop-shadow-lg rounded-lg w-full max-w-md min-h-40 py-8 px-6 mx-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-Montserrat mb-1 text-center text-primaryBlack font-medium text-xl">
              Cannot send service report to multiple companies. Please only
              select one
            </h2>
            <div className="flex justify-center gap-2 pt-3 w-full">
              <button
                type="button"
                className="text-themeBtn flex items-center uppercase font-Proxima text-[15px] justify-center px-6 py-1.5 rounded border border-themeBtn"
                onClick={() => setShowErrorPopup(false)}
              >
                close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ServiceReport;
